import { useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Sparkles, Users, Compass, ArrowRight, ArrowLeft, Check, Rocket, Shield, UserCog, User } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", icon: Shield, tint: "text-[color:var(--accent)] bg-[color:var(--accent)]/12 border-[color:var(--accent)]/50" },
  manager: { label: "Manager", icon: UserCog, tint: "text-[#00e5ff] bg-[#00e5ff]/12 border-[#00e5ff]/50" },
  employee: { label: "Employee", icon: User, tint: "text-slate-200 bg-slate-500/12 border-slate-500/50" },
};

export default function WelcomeCarousel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const brandingCtx = useBranding();
  const branding = brandingCtx?.branding || {};
  const [step, setStep] = useState(0);

  const meta = ROLE_META[user?.role] || ROLE_META.employee;
  const RoleIcon = meta.icon;

  // Brand-red confetti burst — celebrates the "Go to dashboard" tap before we navigate
  const fireConfetti = () => {
    const colors = ["#ff0000", "#ff3b5c", "#ffc700", "#ffffff", "#00e5ff"];
    const defaults = { origin: { y: 0.7 }, colors, startVelocity: 45, spread: 70, ticks: 220, zIndex: 100 };
    // Center burst
    confetti({ ...defaults, particleCount: 90, scalar: 1.1 });
    // Two side cannons, staggered so it feels like a proper "you're in" moment
    setTimeout(() => confetti({ ...defaults, particleCount: 50, angle: 60, spread: 55, origin: { x: 0, y: 0.75 } }), 120);
    setTimeout(() => confetti({ ...defaults, particleCount: 50, angle: 120, spread: 55, origin: { x: 1, y: 0.75 } }), 220);
  };

  const finish = () => {
    fireConfetti();
    // Small delay so a few particles are already on screen before the layout swaps
    setTimeout(() => navigate("/", { replace: true }), 550);
  };

  const steps = [
    {
      id: "welcome",
      icon: Sparkles,
      eyebrow: "You're in",
      title: `Welcome to ${branding.company_name || "the team"}!`,
      body: (
        <>
          <p className="text-slate-400">Hey <strong className="text-slate-200">{user?.first_name || user?.name}</strong> — glad you're here. Let's take a quick 30-second tour of what's yours.</p>
        </>
      ),
    },
    {
      id: "role",
      icon: RoleIcon,
      eyebrow: "Your role",
      title: `You've been set up as ${meta.label}`,
      body: (
        <>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${meta.tint} font-bold text-sm`} data-testid="welcome-role-pill">
            <RoleIcon size={14} /> {meta.label}
          </div>
          {user?.job && (
            <p className="text-slate-400 mt-3">Your job is <strong className="text-slate-200">{user.job}</strong>. If your admin attached a staff agreement, you'll see it on your profile — read it and tap acknowledge to finish onboarding.</p>
          )}
          {!user?.job && (
            <p className="text-slate-400 mt-3">If your admin assigns a specific job later, it'll show up on your profile — with a staff agreement to read and acknowledge.</p>
          )}
        </>
      ),
    },
    {
      id: "actions",
      icon: Compass,
      eyebrow: "First steps",
      title: "Here's what to do first",
      body: (
        <ul className="space-y-3 text-sm text-slate-300 text-left" data-testid="welcome-first-steps">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center shrink-0 text-[color:var(--accent)] font-bold text-xs">1</span>
            <span><strong>Complete your profile</strong> — add an avatar, cover photo, timezone, and birthday so teammates can find you.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center shrink-0 text-[color:var(--accent)] font-bold text-xs">2</span>
            <span><strong>Check your onboarding page</strong> — tick off tasks day-by-day. Anything red is required.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center shrink-0 text-[color:var(--accent)] font-bold text-xs">3</span>
            <span><strong>Say hi in Chat</strong> — introduce yourself, meet your team.</span>
          </li>
        </ul>
      ),
    },
  ];

  const current = steps[step];
  const StepIcon = current.icon;
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen grid place-items-center px-4 py-8 bg-[#0a0d12]" data-testid="welcome-carousel">
      <div className="w-full max-w-md card-surface overflow-hidden fade-in-up">
        {/* Hero banner */}
        <div className="cover-fallback h-32 relative flex items-center justify-center">
          <div className="w-16 h-16 rounded-2xl bg-black/50 backdrop-blur-md border border-white/10 grid place-items-center brand-glow">
            <StepIcon size={30} className="text-[color:var(--accent)]" />
          </div>
        </div>

        <div className="px-6 pt-5 pb-6 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--accent)] font-bold" data-testid={`welcome-step-eyebrow-${step}`}>
              {current.eyebrow} · Step {step + 1} of {steps.length}
            </div>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight mt-1" data-testid={`welcome-step-title-${step}`}>
              {current.title}
            </h1>
          </div>

          <div className="text-sm leading-relaxed min-h-[100px]">{current.body}</div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 pt-2" data-testid="welcome-dots">
            {steps.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
                data-testid={`welcome-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-[color:var(--accent)] shadow-[0_0_10px_var(--accent)]" : "w-1.5 bg-slate-600 hover:bg-slate-400"}`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                data-testid="welcome-back"
                className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1 transition-colors"
              >
                <ArrowLeft size={14} /> Back
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/", { replace: true })}
                data-testid="welcome-skip"
                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                Skip
              </button>
            )}
            <div className="flex-1" />
            {isLast ? (
              <button
                type="button"
                onClick={finish}
                data-testid="welcome-finish"
                className="px-5 py-2.5 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold text-sm inline-flex items-center gap-2 transition-colors"
              >
                <Rocket size={14} /> Go to dashboard
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                data-testid="welcome-next"
                className="px-5 py-2.5 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold text-sm inline-flex items-center gap-2 transition-colors"
              >
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
